/**
 * Spending Challenges Page - Gamified Budget Management
 */
import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  getActiveChallenges,
  getAllChallenges,
  createChallenge,
  cancelChallenge,
  getAllBadges,
  getBadgeProgress,
  getChallengeSuggestions
} from '../lib/api';
import { toast } from 'sonner';
import {
  Target,
  Trophy,
  Flame,
  Plus,
  Loader2,
  TrendingDown,
  Ban,
  CheckCircle2,
  XCircle,
  Sparkles,
  Award,
  Zap,
  ChevronRight,
  Calendar,
  DollarSign,
  BarChart3,
  Gift,
  Crown,
  Medal
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Category icons mapping
const CATEGORY_ICONS = {
  'Shopping': '🛍️',
  'Food': '🍔',
  'Entertainment': '🎬',
  'Transport': '🚗',
  'Travel': '✈️',
  'Health': '💊',
  'Bills': '📄',
  'Education': '📚',
  'Groceries': '🛒',
  'Other': '📦'
};

// Badge tier colors
const TIER_COLORS = {
  'bronze': 'bg-amber-700/20 text-amber-700 border-amber-700/30',
  'silver': 'bg-slate-400/20 text-slate-600 border-slate-400/30',
  'gold': 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  'platinum': 'bg-violet-500/20 text-violet-600 border-violet-500/30'
};

const SpendingChallenges = () => {
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [pastChallenges, setPastChallenges] = useState([]);
  const [badges, setBadges] = useState([]);
  const [badgeProgress, setBadgeProgress] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // Create challenge dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newChallenge, setNewChallenge] = useState({
    name: '',
    category: 'Shopping',
    challenge_type: 'spend_under',
    target_amount: '',
    period: 'monthly',
    description: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRes, pastRes, badgesRes, progressRes, suggestionsRes] = await Promise.all([
        getActiveChallenges(),
        getAllChallenges('completed', 10),
        getAllBadges(),
        getBadgeProgress(),
        getChallengeSuggestions()
      ]);
      
      setActiveChallenges(activeRes.data || []);
      setPastChallenges(pastRes.data?.challenges || []);
      setBadges(badgesRes.data?.badges || []);
      setBadgeProgress(progressRes.data);
      setSuggestions(suggestionsRes.data?.suggestions || []);
    } catch (error) {
      logger.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateChallenge = async () => {
    if (!newChallenge.name || !newChallenge.target_amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    try {
      await createChallenge({
        ...newChallenge,
        target_amount: parseFloat(newChallenge.target_amount)
      });
      toast.success('Challenge created! Let\'s go! 🎯');
      setCreateDialogOpen(false);
      setNewChallenge({
        name: '',
        category: 'Shopping',
        challenge_type: 'spend_under',
        target_amount: '',
        period: 'monthly',
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create challenge');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelChallenge = async (challengeId) => {
    try {
      await cancelChallenge(challengeId);
      toast.success('Challenge cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel challenge');
    }
  };

  const handleUseSuggestion = (suggestion) => {
    setNewChallenge({
      name: suggestion.name,
      category: suggestion.category,
      challenge_type: suggestion.challenge_type || 'spend_under',
      target_amount: suggestion.target_amount.toString(),
      period: suggestion.period,
      description: suggestion.description
    });
    setCreateDialogOpen(true);
  };

  const getProgressColor = (percent, type) => {
    if (type === 'spend_under') {
      if (percent >= 90) return 'bg-red-500';
      if (percent >= 70) return 'bg-amber-500';
      return 'bg-emerald-500';
    }
    return 'bg-emerald-500';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="space-y-8 max-w-6xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="challenges-container"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Target className="w-8 h-8 text-emerald-500" />
              Spending Challenges
            </h1>
            <p className="text-muted-foreground">Gamify your budgeting journey</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-challenge-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Challenge
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-500" />
                  Create Challenge
                </DialogTitle>
                <DialogDescription>
                  Set a spending goal and earn badges
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Challenge Name</Label>
                  <Input
                    placeholder="e.g., Cut Coffee Spending"
                    value={newChallenge.name}
                    onChange={(e) => setNewChallenge(p => ({ ...p, name: e.target.value }))}
                    className="rounded-xl"
                    data-testid="challenge-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={newChallenge.category}
                      onValueChange={(v) => setNewChallenge(p => ({ ...p, category: v }))}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="challenge-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(CATEGORY_ICONS).map(cat => (
                          <SelectItem key={cat} value={cat}>
                            {CATEGORY_ICONS[cat]} {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Challenge Type</Label>
                    <Select
                      value={newChallenge.challenge_type}
                      onValueChange={(v) => setNewChallenge(p => ({ ...p, challenge_type: v }))}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="challenge-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spend_under">
                          <span className="flex items-center gap-2">
                            <TrendingDown className="w-4 h-4" /> Spend Under
                          </span>
                        </SelectItem>
                        <SelectItem value="no_spend">
                          <span className="flex items-center gap-2">
                            <Ban className="w-4 h-4" /> No Spend
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Target Amount ($)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="200"
                        value={newChallenge.target_amount}
                        onChange={(e) => setNewChallenge(p => ({ ...p, target_amount: e.target.value }))}
                        className="pl-9 rounded-xl"
                        disabled={newChallenge.challenge_type === 'no_spend'}
                        data-testid="challenge-amount"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Period</Label>
                    <Select
                      value={newChallenge.period}
                      onValueChange={(v) => setNewChallenge(p => ({ ...p, period: v }))}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="challenge-period">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button
                  onClick={handleCreateChallenge}
                  disabled={creating}
                  className="w-full rounded-xl"
                  data-testid="submit-challenge"
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Target className="w-4 h-4 mr-2" />
                  )}
                  Start Challenge
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Overview */}
        {badgeProgress && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Trophy className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                <p className="text-2xl font-bold" data-testid="completed-count">
                  {badgeProgress.stats.challenges_completed}
                </p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Flame className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                <p className="text-2xl font-bold" data-testid="streak-count">
                  {badgeProgress.stats.current_streak}
                </p>
                <p className="text-xs text-muted-foreground">Day Streak</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                <p className="text-2xl font-bold" data-testid="saved-amount">
                  ${badgeProgress.stats.total_saved}
                </p>
                <p className="text-xs text-muted-foreground">Total Saved</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Award className="w-6 h-6 mx-auto mb-2 text-violet-500" />
                <p className="text-2xl font-bold" data-testid="badges-count">
                  {badgeProgress.stats.badges_earned}
                </p>
                <p className="text-xs text-muted-foreground">Badges</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <BarChart3 className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                <p className="text-2xl font-bold" data-testid="categories-count">
                  {badgeProgress.stats.categories_mastered}
                </p>
                <p className="text-xs text-muted-foreground">Categories</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-12 rounded-xl">
            <TabsTrigger value="active" className="rounded-lg" data-testid="active-tab">
              <Target className="w-4 h-4 mr-2" />
              Active ({activeChallenges.length})
            </TabsTrigger>
            <TabsTrigger value="badges" className="rounded-lg" data-testid="badges-tab">
              <Trophy className="w-4 h-4 mr-2" />
              Badges ({badges.filter(b => b.earned).length})
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg" data-testid="history-tab">
              <Calendar className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Active Challenges Tab */}
          <TabsContent value="active" className="mt-6 space-y-6">
            {/* Suggestions Section */}
            {suggestions.length > 0 && activeChallenges.length < 5 && (
              <Card className="border-dashed border-2 border-emerald-500/30 bg-emerald-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-500" />
                    Suggested Challenges
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {suggestions.slice(0, 3).map((suggestion) => (
                      <motion.div
                        key={`suggestion-${suggestion.category}-${suggestion.name}`}
                        className="flex-shrink-0 w-64 p-4 rounded-xl bg-background border cursor-pointer hover:border-emerald-500 transition-colors"
                        onClick={() => handleUseSuggestion(suggestion)}
                        whileHover={{ scale: 1.02 }}
                        data-testid={`suggestion-${suggestion.category}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl">{CATEGORY_ICONS[suggestion.category] || '📦'}</span>
                          <span className="font-medium text-sm">{suggestion.name}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{suggestion.description}</p>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">
                            ${suggestion.target_amount} / {suggestion.period}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Active Challenges List */}
            {activeChallenges.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Target className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Active Challenges</h3>
                  <p className="text-muted-foreground mb-4">Start a challenge to begin earning badges</p>
                  <Button className="rounded-full" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Challenge
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                <AnimatePresence>
                  {activeChallenges.map((challenge) => (
                    <motion.div
                      key={challenge.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <Card data-testid={`challenge-${challenge.id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center text-2xl flex-shrink-0">
                              {CATEGORY_ICONS[challenge.category] || '📦'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{challenge.name}</h3>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {challenge.period}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">
                                {challenge.challenge_type === 'spend_under' 
                                  ? `Keep ${challenge.category} spending under $${challenge.target_amount}`
                                  : `No spending on ${challenge.category}`
                                }
                              </p>
                              
                              {/* Progress Bar */}
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">
                                    ${challenge.current_amount?.toFixed(2) || '0.00'} / ${challenge.target_amount?.toFixed(2)}
                                  </span>
                                  <span className={`font-medium ${
                                    challenge.progress_percent >= 90 ? 'text-red-500' :
                                    challenge.progress_percent >= 70 ? 'text-amber-500' :
                                    'text-emerald-500'
                                  }`}>
                                    {challenge.progress_percent?.toFixed(0) || 0}% used
                                  </span>
                                </div>
                                <Progress 
                                  value={Math.min(challenge.progress_percent || 0, 100)} 
                                  className={`h-3 [&>div]:${getProgressColor(challenge.progress_percent, challenge.challenge_type)}`}
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>Remaining: ${Math.max(0, (challenge.target_amount || 0) - (challenge.current_amount || 0)).toFixed(2)}</span>
                                  <span>Ends: {new Date(challenge.end_date).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelChallenge(challenge.id)}
                              className="text-muted-foreground hover:text-destructive"
                              data-testid={`cancel-${challenge.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {badges.map((badge) => (
                <motion.div
                  key={badge.id}
                  whileHover={{ scale: 1.05 }}
                  data-testid={`badge-${badge.id}`}
                >
                  <Card className={`relative overflow-hidden ${
                    badge.earned 
                      ? 'border-2 border-emerald-500/50' 
                      : 'opacity-50 grayscale'
                  }`}>
                    <CardContent className="p-4 text-center">
                      <div className="text-4xl mb-2">{badge.icon}</div>
                      <h4 className="font-semibold text-sm mb-1">{badge.name}</h4>
                      <p className="text-xs text-muted-foreground mb-2">{badge.description}</p>
                      <Badge className={TIER_COLORS[badge.tier]}>
                        {badge.tier}
                      </Badge>
                      {badge.earned && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            
            {/* Next Badges to Earn */}
            {badgeProgress?.next_badges?.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Up Next
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {badgeProgress.next_badges.map((next) => (
                      <div key={next.badge_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <Gift className="w-5 h-5 text-amber-500" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{next.badge_id.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                          <p className="text-xs text-muted-foreground">{next.progress}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{next.remaining}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            {pastChallenges.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No completed challenges yet
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {pastChallenges.map((challenge) => (
                  <Card key={challenge.id} className="opacity-80" data-testid={`history-${challenge.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-xl">
                          {CATEGORY_ICONS[challenge.category] || '📦'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{challenge.name}</span>
                            {challenge.status === 'completed' ? (
                              <Badge className="bg-emerald-500/20 text-emerald-600 border-0">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/20 text-red-600 border-0">
                                <XCircle className="w-3 h-3 mr-1" /> Failed
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            ${challenge.current_amount?.toFixed(2)} / ${challenge.target_amount?.toFixed(2)} • 
                            {new Date(challenge.end_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default SpendingChallenges;
